import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logger from '../utils/intelligentLogger';

interface LoadingTask {
  id: string;
  name: string;
  task: () => Promise<void>;
  priority: 'high' | 'medium' | 'low';
  isBackground?: boolean;
  timeout?: number; // Allow custom timeouts
}

interface LoadingContextType {
  isLoading: boolean;
  isBackgroundLoading: boolean;
  currentTask: string | null;
  progress: number;
  addTask: (task: LoadingTask) => void;
  removeTask: (taskId: string) => void;
  clearTasks: () => void;
  preventNavigation: boolean;
  setPreventNavigation: (prevent: boolean) => void;
  // AUGMENT V80.0 - Instant cache functionality
  isTaskCached: (taskName: string) => boolean;
  markTaskAsCached: (taskName: string) => void;
  addSimpleTask: (taskName: string) => string;
  completeSimpleTask: (taskId: string) => void;
}

// AUGMENT V80.0 - Persistent cache across navigation
const taskCache = new Set<string>();
const completedTasks = new Set<string>();

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<LoadingTask[]>([]);
  const [currentTask, setCurrentTask] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [preventNavigation, setPreventNavigation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Track if we're currently loading
  const isLoading = tasks.length > 0;
  const isBackgroundLoading = tasks.some(task => task.isBackground);

  // AUGMENT V80.0 - Instant cache functionality
  const isTaskCached = useCallback((taskName: string) => {
    return taskCache.has(taskName) || completedTasks.has(taskName);
  }, []);

  const markTaskAsCached = useCallback((taskName: string) => {
    taskCache.add(taskName);
    completedTasks.add(taskName);
  }, []);

  // AUGMENT V80.0 - Simple task management for instant cache
  const addSimpleTask = useCallback((taskName: string) => {
    // Check if task is already cached - if so, skip it
    if (isTaskCached(taskName)) {
      Logger.debug(`Task skipped (cached): ${taskName}`);
      return '';
    }

    const taskId = `simple_task_${Date.now()}_${Math.random()}`;
    Logger.debug(`Adding simple task: ${taskName}`);
    return taskId;
  }, [isTaskCached]);

  const completeSimpleTask = useCallback((taskId: string) => {
    if (!taskId) return;
    // Extract task name from context and mark as cached
    // This is a simplified version for instant cache
    Logger.debug(`Simple task completed: ${taskId}`);
  }, []);

  // Add a new task
  const addTask = useCallback((task: LoadingTask) => {
    setTasks(prev => {
      // Check if task with same ID already exists
      const existingTask = prev.find(t => t.id === task.id);
      if (existingTask) {
        console.log(`Task ${task.name} already exists, skipping...`);
        return prev;
      }

      console.log(`Adding task: ${task.name}`);
      return [...prev, task];
    });

    // If it's a high priority task, prevent navigation
    if (task.priority === 'high') {
      setPreventNavigation(true);
    }
  }, []);

  // Remove a completed task
  const removeTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  // Clear all tasks
  const clearTasks = useCallback(() => {
    setTasks([]);
    setCurrentTask(null);
    setProgress(0);
    setPreventNavigation(false);
  }, []);

  // Process tasks with optimized performance
  useEffect(() => {
    if (tasks.length === 0) {
      setCurrentTask(null);
      setProgress(0);
      setPreventNavigation(false);
      return;
    }

    const processTasks = async () => {
      // Sort tasks by priority
      const sortedTasks = [...tasks].sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      for (let i = 0; i < sortedTasks.length; i++) {
        const task = sortedTasks[i];
        setCurrentTask(task.name);
        setProgress((i / sortedTasks.length) * 100);

        try {
          // Use custom timeout or default to faster timeout
          const timeout = task.timeout || (task.isBackground ? 15000 : 5000); // 15s for background, 5s for foreground
          const taskPromise = task.task();
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Task timeout')), timeout);
          });

          await Promise.race([taskPromise, timeoutPromise]);
          // Production clean - no console logging
          // AUGMENT V80.0 - Mark task as cached when completed
          markTaskAsCached(task.name);
          removeTask(task.id);
        } catch (error) {
          console.error(`❌ Task failed: ${task.name}`, error);
          removeTask(task.id);
        }
      }
    };

    processTasks();
  }, [tasks, removeTask]);

  // Prevent navigation during critical loading
  useEffect(() => {
    if (preventNavigation) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };

      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        // Stay on current page
        window.history.pushState(null, '', location.pathname);
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [preventNavigation, location.pathname]);

  const value: LoadingContextType = {
    isLoading,
    isBackgroundLoading,
    currentTask,
    progress,
    addTask,
    removeTask,
    clearTasks,
    preventNavigation,
    setPreventNavigation,
    // AUGMENT V80.0 - Instant cache functionality
    isTaskCached,
    markTaskAsCached,
    addSimpleTask,
    completeSimpleTask,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}; 